import { ReactNode } from "react";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import {
  SampleBaseData,
  samplesBaseDataSubdomain,
} from "@/services/backend/samples/types/sample-base-data";
import { DefaultBaseDataCheckbox } from "@/services/backend/shared/base-data-kit/components/default-base-data-checkbox";
import { useListCompanyQuery } from "@/services/backend/company/api/company";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { Button } from "@/shared/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { formatDateTime } from "@/shared/lib/utilities/date";

/**
 * The list request every sample base data kind shares. It is assignable to
 * the `ListRequest` of each kind's api, which only differ in the columns
 * they can be ordered by.
 */
export interface SampleBaseDataListRequest {
  /**
   * Required by the backend: it must be active and hold at least one
   * company, otherwise the request fails its own validation.
   */
  company: Filter<UUID>;
  label?: Filter<string>;
  limit?: number;
  offset?: number;
}

export function useDefaultSampleBaseDataListRequest(): SampleBaseDataListRequest {
  return {
    label: { active: false, values: [] },
    company: { active: false, values: [] },
    limit: 50,
    offset: 0,
  };
}

/**
 * Returns the request to send for the list request the user configured.
 *
 * The backend requires an active company filter with at least one company,
 * so an inactive filter falls back to every company the user may see. Until
 * those are known there is no request to send, which `skip` reports.
 */
export function useSampleBaseDataListRequestWithCompanies(
  request: SampleBaseDataListRequest,
): { request: SampleBaseDataListRequest; skip: boolean } {
  const { data: companyList } = useListCompanyQuery({});
  const companyIds = companyList?.companies.map((c) => c.id) ?? [];
  const companiesKnown = companyIds.length > 0;

  return {
    request: request.company.active
      ? request
      : { ...request, company: { active: true, values: companyIds } },
    skip: !request.company.active && !companiesKnown,
  };
}

/**
 * Renders a page of sample base data of one kind. The kind specific tables
 * query the list and pass the result, see e.g.
 * `AirMicrobialBaseDataTable`.
 */
export function SampleBaseDataTable<T extends SampleBaseData>({
  data,
  isLoading,
  error,
  dataKey,
  request,
  onRequestChange,
  renderRowLink,
  renderDeleteAction,
  hidePagination = false,
  hideBorder = false,
}: {
  data: Lst<T> | undefined;
  isLoading: boolean;
  error: unknown;
  /** The `basedatakit.Default` key of the kind, e.g. `airMicrobialBaseDataKey`. */
  dataKey: string;
  request: SampleBaseDataListRequest;
  onRequestChange: (request: SampleBaseDataListRequest) => void;
  renderRowLink: (baseDataId: string, children: ReactNode) => ReactNode;
  renderDeleteAction: (baseData: T) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const emptyList = !isLoading && !error && data?.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Aktiv")}</TableHead>
              <TableHead>{t("Bezeichnung")}</TableHead>
              <TableHead>{t("Unternehmen")}</TableHead>
              <TableHead>{t("Aktualisiert am")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={5} />}
            {error !== undefined && error !== null && (
              <TableRtkErrorRow error={error} colSpan={5} />
            )}
            {emptyList && <TableEmptyRow colSpan={5} />}
            {data &&
              data.items.map((baseData) => (
                <TableRow key={baseData.id}>
                  <TableCell>
                    <div className="ml-4">
                      <DefaultBaseDataCheckbox
                        subdomain={samplesBaseDataSubdomain}
                        dataKey={dataKey}
                        companyId={baseData.companyId}
                        id={baseData.id}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    {renderRowLink(
                      baseData.id,
                      <Button variant="link">{baseData.label}</Button>,
                    )}
                  </TableCell>
                  <TableCell>
                    <CompanyName companyId={baseData.companyId} />
                  </TableCell>
                  <TableCell>{formatDateTime(baseData.updatedAt)}</TableCell>
                  <TableCell>{renderDeleteAction(baseData)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && data && !emptyList && (
        <div className="flex items-center">
          <PaginationLimitOffset
            offset={request.offset ?? 0}
            limit={request.limit ?? 0}
            totalCount={data.totalCount}
            setLimitOffset={(limit, offset) =>
              onRequestChange({ ...request, limit, offset })
            }
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}

export function SampleBaseDataTableFilters({
  request,
  onRequestChange,
  hideCompanyFilter,
}: {
  request: SampleBaseDataListRequest;
  onRequestChange: (request: SampleBaseDataListRequest) => void;
  hideCompanyFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <SearchFilter
        filter={request.label ?? { active: false, values: [] }}
        onFilterChange={(label) => onRequestChange({ ...request, label })}
        placeholder={t("Nach Bezeichnung suchen ...")}
        className="h-8"
      />
      <CompanyFilter
        filter={request.company}
        onFilterChange={(company) => onRequestChange({ ...request, company })}
        hidden={hideCompanyFilter}
      />
    </div>
  );
}
